import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  KeyRound,
  ClipboardCheck,
  Clock,
  TrendingUp,
  ListChecks,
  MonitorCheck,
  ShieldCheck,
  UserCog,
  Wallet,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Cpu,
  Lock,
  Target,
  LineChart,
  Server,
} from 'lucide-react';

const features = [
  {
    icon: KeyRound,
    title: 'Smart Student Login',
    desc: 'Every student gets a unique User ID and password to securely access our computer lab. Only registered students can use the systems.',
  },
  {
    icon: ClipboardCheck,
    title: 'Automatic Attendance',
    desc: 'Attendance is recorded the moment a student logs in. No manual registers, 100% accurate tracking.',
  },
  {
    icon: Clock,
    title: 'Time Managed Learning',
    desc: 'Dedicated lab hours per student. The system manages practice sessions and logout timing, building punctuality and discipline.',
  },
  {
    icon: TrendingUp,
    title: 'Live Progress Tracking',
    desc: "Teachers stay close to every student's journey, so parents can be confident each module is completed systematically.",
  },
  {
    icon: ListChecks,
    title: 'Structured Course Completion',
    desc: 'Students learn through a predefined syllabus. Each completed module is digitally tracked to ensure complete learning.',
  },
  {
    icon: MonitorCheck,
    title: 'Professional Computer Lab',
    desc: 'Our lab runs through a centralized management system, creating a calm, distraction-free space for every student.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure Computer Access',
    desc: 'Unauthorized usage is prevented. Every session is protected through secure authentication.',
  },
  {
    icon: UserCog,
    title: 'Personalized Learning Progress',
    desc: 'Attendance, lab hours, course progress, completed modules, practice time and learning performance — guided individually for every student.',
  },
  {
    icon: Wallet,
    title: 'Smart Fee Management',
    desc: 'Digital fee records and timely reminders ensure a transparent, organized, stress-free experience for students and parents.',
  },
  {
    icon: Sparkles,
    title: 'Smart Learning Environment',
    desc: 'A professional setting where technology drives discipline, productivity and better learning outcomes for every student.',
  },
];

const environmentPoints = [
  'Smart Student Login',
  'Automatic Attendance',
  'Digital Progress Tracking',
  'Structured Course Completion',
  'Guided Student Development',
  'Secure Computer Lab',
  'Dedicated Practice Hours',
  'Smart Fee Management',
  'Professional Learning Environment',
  'Technology Driven Education',
];

const pillars = [
  { icon: ShieldCheck, label: 'Secure Learning Environment' },
  { icon: LineChart, label: '100% Digital Student Tracking' },
  { icon: Clock, label: 'Smart Lab Time Management' },
  { icon: Target, label: 'Structured Course Completion' },
  { icon: Lock, label: 'Secure Login Access' },
  { icon: Cpu, label: 'Technology Powered Institute' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.5, ease: 'easeOut' },
  }),
};

const WhyChooseSection = () => {
  return (
    <section
      id="why-choose"
      aria-labelledby="why-choose-heading"
      className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white"
      data-testid="why-choose-section"
    >
      {/* Decorative gradient blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 bg-cyan-200/40 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] bg-blue-300/30 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 right-10 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-20"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold text-cyan-700 bg-cyan-100/70 backdrop-blur-sm border border-cyan-200/60 mb-5"
            data-testid="why-choose-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Why Choose Genius Academy
          </span>
          <h2
            id="why-choose-heading"
            className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4"
            data-testid="why-choose-heading"
          >
            Why Thousands of Students <span className="text-cyan-600">Trust Genius Academy</span>
          </h2>
          <p className="text-sm md:text-base text-cyan-700 font-semibold mb-6 max-w-3xl mx-auto">
            Technology-Driven Learning &nbsp;|&nbsp; Complete Discipline &nbsp;|&nbsp; Professional Environment &nbsp;|&nbsp; Smart Student Management
          </p>
          <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            At Genius Academy, we don't just teach computer skills — we provide a highly disciplined, secure, and
            technology-driven learning environment. Our institute uses a custom-built{' '}
            <span className="font-semibold text-slate-900">Smart Student Management System</span> developed in-house to ensure
            every student's learning journey is structured, guided, and productive.
          </p>
        </motion.div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 md:gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.article
                key={feature.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-60px' }}
                whileHover={{ y: -6 }}
                className="group relative rounded-2xl p-6 backdrop-blur-xl bg-white/70 border border-white/60 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_48px_-16px_rgba(8,145,178,0.35)] hover:border-cyan-200 transition-all duration-300"
                data-testid={`why-feature-${i + 1}`}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-50/0 via-transparent to-blue-50/0 group-hover:from-cyan-50/60 group-hover:to-blue-50/40 transition-all duration-500 pointer-events-none" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md shadow-cyan-500/20 mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.25} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 leading-snug">
                    <span className="text-cyan-600 mr-1">{String(i + 1).padStart(2, '0')}.</span> {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Smart Learning Environment Block */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="relative mt-20 md:mt-28 rounded-3xl overflow-hidden border border-white/60 backdrop-blur-2xl bg-white/70 shadow-[0_20px_60px_-20px_rgba(8,145,178,0.25)] p-8 md:p-14"
          data-testid="smart-environment-block"
        >
          <div aria-hidden="true" className="absolute -top-24 -right-24 w-80 h-80 bg-gradient-to-br from-cyan-300/40 to-blue-400/30 rounded-full blur-3xl" />
          <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-80 h-80 bg-gradient-to-tr from-orange-200/40 to-pink-200/30 rounded-full blur-3xl" />

          <div className="relative grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold text-cyan-700 bg-cyan-100 border border-cyan-200 mb-4">
                <Server className="w-3.5 h-3.5" /> In-House Built
              </span>
              <h3 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight">
                Our Smart <span className="text-cyan-600">Learning Environment</span>
              </h3>
              <p className="text-slate-600 leading-relaxed">
                A premium ecosystem engineered for focus, accountability and growth — every part working together so
                students learn with confidence and parents stay informed.
              </p>
            </div>

            <ul className="lg:col-span-3 grid sm:grid-cols-2 gap-3" data-testid="smart-environment-list">
              {environmentPoints.map((point, i) => (
                <motion.li
                  key={point}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="flex items-center gap-3 rounded-xl px-4 py-3 bg-white/80 border border-slate-100 hover:border-cyan-200 hover:bg-white transition-all"
                  data-testid={`env-point-${i + 1}`}
                >
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow shadow-cyan-500/20">
                    <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </span>
                  <span className="font-medium text-slate-800 text-sm md:text-base">{point}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Genius Academy Experience pillars */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mt-20 md:mt-28"
        >
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-4xl font-bold text-slate-900">
              The Genius Academy <span className="text-cyan-600">Experience</span>
            </h3>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Six pillars that define a premium, professional and future-ready computer training journey.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6" data-testid="experience-pillars">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-5 text-center backdrop-blur-xl bg-white/70 border border-white/60 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all"
                  data-testid={`pillar-${i + 1}`}
                >
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center mb-3 shadow-md">
                    <Icon className="w-6 h-6 text-cyan-300" strokeWidth={2.25} />
                  </div>
                  <p className="text-sm font-semibold text-slate-800 leading-snug">{p.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Premium banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="relative mt-20 md:mt-28 rounded-3xl overflow-hidden p-10 md:p-16 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white shadow-2xl"
          data-testid="more-than-banner"
        >
          <div aria-hidden="true" className="absolute -top-32 -right-32 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl" />
          <div aria-hidden="true" className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/20 backdrop-blur-sm mb-5">
              <GraduationCap className="w-4 h-4 text-cyan-300" />
              Beyond a Computer Institute
            </div>
            <h3 className="text-3xl md:text-5xl font-bold mb-5 leading-tight">
              More Than Just a <span className="text-cyan-300">Computer Institute</span>
            </h3>
            <p className="text-base md:text-lg text-slate-200 leading-relaxed mb-3">
              At Genius Academy, technology is not only <em>what</em> we teach — it's <em>how</em> we teach.
            </p>
            <p className="text-sm md:text-base text-slate-300 leading-relaxed">
              Our custom-developed Smart Student Management System creates a structured, disciplined, transparent and
              productive learning environment where every student's attendance, progress, practice time and course
              completion are managed digitally. This lets students focus entirely on learning while giving parents
              complete confidence in our professional teaching methodology.
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mt-16 md:mt-20 text-center"
          data-testid="why-choose-cta"
        >
          <h3 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3">
            Experience the <span className="text-cyan-600">Future of Computer Education</span>
          </h3>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            Join the most disciplined and technology-driven computer training institute in the region.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              data-testid="why-choose-enroll"
            >
              Enroll Now <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-full px-8 py-4 font-bold transition-all"
              data-testid="why-choose-demo"
            >
              Book Free Demo Class
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
